import { nextTick, watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import { i18n } from '@/i18n'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { useUiStore } from '@/stores/ui'
import config from '@/config'
import { resolveDefaultRoute, resolvePostLoginRoute } from '@/lib/defaultRoute'
import { canAccessRouteName, canMeetRouteRbac } from '@/lib/routeAccess'
import SsoLogin from '@/views/login/sso.vue'
import Home from '@/views/home.vue'
import Login from '@/views/login.vue'
import { routes as docviewerRoutes } from 'soykajs/components/docviewer'

const router = createRouter({
  // Runtime-конфиг может содержать BASE_URL: undefined и перезаписать
  // значение Vite. Без fallback Vue Router строит адрес вида undefined/.
  history: createWebHistory(config.BASE_URL || import.meta.env.BASE_URL || '/'),
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
      meta: { titleKey: 'routes.home', rbac: 'home_view' },
    },
    {
      path: '/main',
      name: 'main',
      component: () => import('@/views/main.vue'),
      meta: {
        titleKey: 'routes.main',
        icon: 'house',
        sidebar: true,
        // Viewer без SoH / Бухгалтер / Координатор / GxP открывают ГС.
        rbac: ['submissions_view', 'viewer', 'buh', 'regcoord', 'gxp'],
      },
    },
    {
      path: '/products',
      name: 'products.list',
      component: () => import('@/views/products/list.vue'),
      meta: {
        titleKey: 'routes.products.list',
        icon: 'box-seam',
        sidebar: true,
        rbac: 'products_view',
      },
    },
    {
      path: '/products/new',
      name: 'products.new',
      component: () => import('@/views/products/item.vue'),
      props: () => ({ id: 0 }),
      meta: { titleKey: 'routes.products.new', rbac: 'products_edit' },
    },
    {
      path: '/products/:id(\\d+)',
      name: 'products.item',
      component: () => import('@/views/products/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.products.item', rbac: 'products_view' },
    },
    {
      path: '/product-cards/:id(\\d+)',
      name: 'productCards.item',
      component: () => import('@/views/productCards/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.productCards.item' },
    },
    {
      path: '/submissions',
      name: 'submissions.list',
      component: () => import('@/views/submissions/list.vue'),
      meta: {
        titleKey: 'routes.submissions.list',
        icon: 'file-earmark-text',
        sidebar: true,
        rbac: 'submissions_view',
      },
    },
    {
      path: '/submissions/new',
      name: 'submissions.new',
      component: () => import('@/views/submissions/item.vue'),
      props: () => ({ id: 0 }),
      meta: { titleKey: 'routes.submissions.new', rbac: 'submissions_edit' },
    },
    {
      path: '/submissions/:id(\\d+)',
      name: 'submissions.item',
      component: () => import('@/views/submissions/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.submissions.item', rbac: 'submissions_view' },
    },
    {
      path: '/gmp',
      name: 'gmp.list',
      meta: {
        titleKey: 'routes.gmp.list',
        icon: 'shield-check',
        sidebar: true,
      },
      children: [
        {
          path: '/gmp_status_list',
          name: 'gmpStatusList.list',
          component: () => import('@/views/gmpStatusList/list.vue'),
          meta: {
            titleKey: 'routes.gmpStatusList.list',
            sidebar: true,
            rbac: 'gmp_status_view',
          },
        },
        {
          path: '/gmp_status_list/new',
          name: 'gmpStatusList.new',
          component: () => import('@/views/gmpStatusList/item.vue'),
          props: () => ({ id: 0 }),
          meta: { titleKey: 'routes.gmpStatusList.new' },
        },
        {
          path: '/gmp_status_list/:id(\\d+)',
          name: 'gmpStatusList.item',
          component: () => import('@/views/gmpStatusList/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { titleKey: 'routes.gmpStatusList.item' },
        },
        {
          path: '/gmp_for_submission',
          name: 'gmpForSubmission.list',
          component: () => import('@/views/gmpForSubmission/list.vue'),
          meta: {
            titleKey: 'routes.gmpForSubmission.list',
            sidebar: true,
            rbac: 'gmp_submission_view',
          },
        },
        {
          path: '/gmp_for_submission/new',
          name: 'gmpForSubmission.new',
          component: () => import('@/views/gmpForSubmission/item.vue'),
          props: () => ({ id: 0 }),
          meta: { titleKey: 'routes.gmpForSubmission.new' },
        },
        {
          path: '/gmp_for_submission/:id(\\d+)',
          name: 'gmpForSubmission.item',
          component: () => import('@/views/gmpForSubmission/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { titleKey: 'routes.gmpForSubmission.item' },
        },
        {
          path: '/sites',
          name: 'site.list',
          component: () => import('@/views/site/list.vue'),
          meta: {
            titleKey: 'routes.site.list',
            sidebar: true,
            rbac: 'sites_view',
          },
        },
        {
          path: '/sites/new',
          name: 'site.new',
          component: () => import('@/views/site/item.vue'),
          props: () => ({ id: 0 }),
          meta: { titleKey: 'routes.site.new' },
        },
        {
          path: '/sites/:id(\\d+)',
          name: 'site.item',
          component: () => import('@/views/site/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { titleKey: 'routes.site.item' },
        },
      ],
    },
    {
      path: '/taxes',
      name: 'taxes.list',
      component: () => import('@/views/taxes/list.vue'),
      meta: {
        titleKey: 'routes.taxes.list',
        icon: 'clock-history',
        sidebar: true,
        rbac: 'taxes_view',
      },
    },
    {
      path: '/taxes/new',
      name: 'taxes.new',
      component: () => import('@/views/taxes/item.vue'),
      props: () => ({ id: 0 }),
      meta: { titleKey: 'routes.taxes.new' },
    },
    {
      path: '/taxes/:id(\\d+)',
      name: 'taxes.item',
      component: () => import('@/views/taxes/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.taxes.item' },
    },
    {
      path: '/finder/projects/view',
      name: 'finder.projectsView',
      component: () => import('@/views/finder/projects-view.vue'),
      meta: { hideAppChrome: true },
    },
    {
      path: '/finder',
      name: 'finder.list',
      // FinderList в KeepAlive (app.vue): возврат с /docview без полного ремаунта.
      component: () => import('@/views/finder/list.vue'),
      meta: {
        titleKey: 'routes.finder.list',
        icon: 'folder2-open',
        sidebar: true,
        rbac: [
          'catalog_view',
          'regspec',
          'registration_specialist',
          'manager',
          'supermanager',
          'coordinator',
          'dossier_view',
          'dossier_taxes_memos',
          'dossier_presentations_view',
          'globalteam',
        ],
      },
    },
    {
      path: '/finder/pdf-editor',
      name: 'finder.pdfEditor',
      component: () => import('@/views/finder/pdf-editor.vue'),
      // Компонент этого роута (FinderPdfEditor, views/finder/pdf-editor.vue) кэшируется
      // через KeepAlive в app.vue (KEEP_ALIVE_COMPONENTS) — обычный ремаунт при навигации
      // туда-обратно ломает прототип PdfEditor.vue (топ-уровневые const/let классического
      // legacy.js переобъявляются повторной инъекцией скрипта). См.
      // docs/pdf-editor/implementation.md.
      meta: {
        titleKey: 'routes.finder.pdfEditor',
        sidebar: false,
        rbac: [
          'catalog_view',
          'regspec',
          'registration_specialist',
          'manager',
          'supermanager',
          'coordinator',
          'dossier_view',
          'dossier_taxes_memos',
          'dossier_presentations_view',
          'globalteam',
        ],
      },
    },
    {
      path: '/remote-dossier-projects',
      name: 'remoteProjects.list',
      component: () => import('@/views/remoteDossierProjects.vue'),
      meta: {
        titleKey: 'routes.remote.dossierProjects',
        icon: 'folder',
        sidebar: true,
        rbac: 'remote_dossier_projects',
      },
    },
    {
      path: '/remote-access',
      name: 'remoteManagement.list',
      component: () => import('@/views/remoteAccess.vue'),
      meta: {
        titleKey: 'routes.remote.management',
        icon: 'display',
        sidebar: true,
        rbac: 'remote_manage',
      },
    },
    {
      path: '/eaeu-product-contacts',
      name: 'eaeuProductContacts.list',
      component: () => import('@/views/eaeuProductContacts/list.vue'),
      meta: {
        titleKey: 'routes.eaeuProductContacts.list',
        icon: 'people',
        sidebar: true,
        rbac: 'eaeu_contacts_view',
      },
    },
    {
      path: '/eaeu-product-contacts/new',
      name: 'eaeuProductContacts.new',
      component: () => import('@/views/eaeuProductContacts/item.vue'),
      props: () => ({ id: 0 }),
      meta: { titleKey: 'routes.eaeuProductContacts.new' },
    },
    {
      path: '/eaeu-product-contacts/:id(\\d+)',
      name: 'eaeuProductContacts.item',
      component: () => import('@/views/eaeuProductContacts/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.eaeuProductContacts.item', rbac: 'eaeu_contacts_view' },
    },
    {
      path: '/documents',
      name: 'documents.list',
      meta: {
        titleKey: 'routes.documents.list',
        icon: 'file-earmark-text',
        sidebar: true,
        rbac: 'documents_view',
      },
      children: [
        {
          path: '/documents/service-notes',
          name: 'documents.serviceNotes.list',
          component: () => import('@/views/documents/serviceNotesList.vue'),
          meta: {
            titleKey: 'routes.documents.serviceNotes.list',
            sidebar: true,
            rbac: 'documents_view',
          },
        },
      ],
    },
    {
      path: '/documents/service-notes/:id(\\d+)',
      name: 'documents.serviceNotes.item',
      component: () => import('@/views/documents/serviceNotesItem.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: {
        titleKey: 'routes.documents.serviceNotes.item',
        rbac: 'documents_view',
      },
    },
    {
      path: '/catalog',
      name: 'catalog.list',
      meta: {
        titleKey: 'routes.catalog.list',
        icon: 'journal-bookmark',
        sidebar: true,
        rbac: [
          'sa',
          'users_edit',
          'catalog_view',
          'catalog_edit',
          'medicines_view',
          'medicines_edit',
          'trademarks_view',
          'trademarks_edit',
        ],
      },
      children: [
        {
          path: '/catalog/medicine_trademarks',
          name: 'catalog.medicine_trademarks.list',
          component: () => import('@/views/catalog/medicine_trademarks/list.vue'),
          meta: {
            titleKey: 'routes.catalog.medicine_trademarks.list',
            sidebar: true,
            rbac: ['catalog_view', 'catalog_edit', 'trademarks_view', 'trademarks_edit'],
          },
        },
        {
          path: '/catalog/medicine_trademarks/:id(\\d+)?',
          name: 'catalog.medicine_trademarks.item',
          component: () => import('@/views/catalog/medicine_trademarks/item.vue'),
          props: rt => ({ id: rt.params.id != null ? Number(rt.params.id) : null }),
          meta: {
            titleKey: 'routes.catalog.medicine_trademarks.item',
            rbac: ['catalog_view', 'catalog_edit', 'trademarks_view', 'trademarks_edit'],
          },
        },
        {
          path: '/catalog/medicines',
          name: 'catalog.medicines.list',
          component: () => import('@/views/catalog/medicines/list.vue'),
          meta: {
            titleKey: 'routes.catalog.medicines.list',
            sidebar: true,
            rbac: ['catalog_view', 'catalog_edit', 'medicines_view', 'medicines_edit'],
          },
        },
        {
          path: '/catalog/medicines/:id(\\d+)?',
          name: 'catalog.medicines.item',
          component: () => import('@/views/catalog/medicines/item.vue'),
          props: rt => ({ id: rt.params.id != null ? Number(rt.params.id) : null }),
          meta: {
            titleKey: 'routes.catalog.medicines.item',
            rbac: ['catalog_view', 'catalog_edit', 'medicines_view', 'medicines_edit'],
          },
        },
        {
          path: '/dropdowns',
          name: 'dropdowns.list',
          component: () => import('@/views/dropdowns/list.vue'),
          meta: {
            titleKey: 'routes.dropdowns.list',
            sidebar: true,
            rbac: ['sa', 'catalog_edit', 'reglead'],
          },
        },
        {
          path: '/catalog/templates',
          name: 'templates.list',
          component: () => import('@/views/templates/list.vue'),
          meta: {
            titleKey: 'routes.templates.list',
            sidebar: true,
            rbac: ['sa', 'catalog_view', 'catalog_edit'],
          },
        },
        {
          path: '/catalog/templates/:id(\\d+)',
          name: 'templates.item',
          component: () => import('@/views/templates/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: {
            titleKey: 'routes.templates.item',
            rbac: ['sa', 'catalog_view', 'catalog_edit'],
          },
        },
        {
          path: '/users',
          name: 'users.list',
          component: () => import('@/views/users/list.vue'),
          meta: { titleKey: 'routes.users.list', sidebar: true, rbac: 'users_edit' },
        },
        {
          path: '/users/new',
          name: 'users.new',
          component: () => import('@/views/users/item.vue'),
          props: () => ({ id: 0 }),
          meta: { titleKey: 'routes.users.new' },
        },
        {
          path: '/users/:id(\\d+)',
          name: 'users.item',
          component: () => import('@/views/users/item.vue'),
          props: rt => ({ id: Number(rt.params.id) }),
          meta: { titleKey: 'routes.users.item' },
        },
        {
          path: '/users-updating',
          name: 'users.updating',
          component: () => import('@/views/users/usersUpdating.vue'),
          meta: {
            titleKey: 'routes.users.updating',
            sidebar: true,
            rbac: 'account_updater',
          },
        },
        {
          path: '/current-user/',
          name: 'profile.item',
          component: () => import('@/views/users/profile.vue'),
          meta: { titleKey: 'routes.profile.item' },
        },
      ],
    },
    {
      path: '/registry-import',
      name: 'registryImport',
      component: () => import('@/views/registryImport/index.vue'),
      meta: {
        titleKey: 'routes.registryImport',
        icon: 'upload',
        sidebar: true,
        rbac: 'sa',
      },
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/dossier',
      name: 'dossier.list',
      component: () => import('@/views/notFound.vue'),
      meta: {
        titleKey: 'routes.dossier.list',
        icon: 'folder2-open',
        sidebar: false,
        rbac: ['dossier_view', 'dossier_taxes_memos', 'dossier_presentations_view', 'globalteam'],
      },
    },
    {
      path: '/analytics',
      name: 'analytics.list',
      component: () => import('@/views/analytics/list.vue'),
      props: rt => ({ reportType: String(rt.query.reportType || '') }),
      meta: {
        titleKey: 'routes.analytics.list',
        icon: 'bar-chart',
        sidebar: true,
        rbac: ['regspec', 'reglead', 'sa'],
      },
    },
    {
      path: '/presentations/committee',
      name: 'presentations.committee',
      component: () => import('@/views/presentations/periodBrowser.vue'),
      props: () => ({ folderKey: 'committee' }),
      meta: {
        titleKey: 'routes.presentations.committee',
        sidebar: false,
        rbac: ['home_view', 'dossier_presentations_view', 'regspec', 'reglead', 'sa'],
      },
    },
    {
      path: '/presentations/reg-intel',
      name: 'presentations.regIntel',
      component: () => import('@/views/presentations/periodBrowser.vue'),
      props: () => ({ folderKey: 'regIntel' }),
      meta: {
        titleKey: 'routes.presentations.regIntel',
        sidebar: false,
        rbac: ['home_view', 'dossier_presentations_view', 'regspec', 'reglead', 'sa'],
      },
    },
    {
      path: '/sops',
      name: 'sops.list',
      component: () => import('@/views/sops/list.vue'),
      meta: {
        titleKey: 'routes.sops.list',
        sidebar: false,
        rbac: ['home_view', 'dossier_presentations_view', 'regspec', 'reglead', 'sa'],
      },
    },
    {
      path: '/rsd',
      name: 'rsd.list',
      component: () => import('@/views/rsd/list.vue'),
      meta: {
        titleKey: 'routes.rsd.list',
        icon: 'asterisk',
        sidebar: false,
        rbac: 'rsd_view',
      },
    },
    {
      path: '/rsd/new',
      name: 'rsd.new',
      component: () => import('@/views/rsd/item.vue'),
      props: () => ({ id: 0 }),
      meta: { titleKey: 'routes.rsd.new', sidebar: false },
    },
    {
      path: '/rsd/:id(\\d+)',
      name: 'rsd.item',
      component: () => import('@/views/rsd/item.vue'),
      props: rt => ({ id: Number(rt.params.id) }),
      meta: { titleKey: 'routes.rsd.item', sidebar: false },
    },
    {
      path: '/sso_login',
      name: 'sso_login',
      component: SsoLogin,
    },
    ...docviewerRoutes,
    {
      path: '/:notFoundPath(.*)',
      name: 'NotFound',
      component: () => import('@/views/notFound.vue'),
    },
  ],
  strict: true,
  scrollBehavior(to, from) {
    if (to.name === from.name) {
      return null
    }
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(to => {
  const userStore = useUserStore()
  const { isAuthorized } = storeToRefs(userStore)
  const { returnUrl } = storeToRefs(useUiStore())

  if (to.name === 'sso_login') {
    return true
  }

  if (isAuthorized.value && to.name === 'login') {
    const target = resolvePostLoginRoute(userStore, returnUrl.value)
    returnUrl.value = undefined
    return target
  }

  // Сохраняем returnUrl только один раз при первом неавторизованном доступе
  if (!isAuthorized.value && to.name !== 'login' && to.name !== 'sso_login') {
    if (returnUrl.value === undefined) {
      returnUrl.value = to
    }
    return { name: 'login' }
  }

  const leafMeta = to.matched[to.matched.length - 1]?.meta

  if (
    isAuthorized.value &&
    leafMeta?.rbac &&
    !canMeetRouteRbac(to.name, leafMeta.rbac, userStore)
  ) {
    const fallback = resolveDefaultRoute(userStore)
    if (to.name !== fallback.name) {
      return fallback
    }
  }

  if (isAuthorized.value && !canAccessRouteName(to.name, userStore)) {
    const fallback = resolveDefaultRoute(userStore)
    if (to.name !== fallback.name) {
      return fallback
    }
  }

  if (
    isAuthorized.value &&
    to.name === 'users.item' &&
    to.params.id &&
    userStore.can('account_updater') &&
    !userStore.can('sa') &&
    !userStore.can('users_edit')
  ) {
    return { name: 'users.updating' }
  }
})

const defaultTitle = 'SOYKA:LTMS'

/**
 * Выставляет заголовок вкладки браузера по `meta.titleKey` (или `meta.title`) маршрута.
 *
 * @param {object} route маршрут vue-router.
 */
function applyDocumentTitle(route) {
  const pageTitle = route.meta.titleKey
    ? i18n.global.t(String(route.meta.titleKey))
    : route.meta.title
  document.title = pageTitle ? `${pageTitle} :: ${defaultTitle}` : defaultTitle
}

router.afterEach(async to => {
  await nextTick()
  applyDocumentTitle(to)
})

// Смена языка не вызывает навигацию: без этого заголовок вкладки остаётся на прежнем языке.
watch(i18n.global.locale, () => applyDocumentTitle(router.currentRoute.value))

export default router
